/**
 * TrialContext — centralises the domain-trial state for dashboard UI.
 *
 * Fires a fetch on mount + on window focus + when apiClient dispatches
 * the 'trial:locked' event (e.g. after a 402 from a cost-bearing action).
 *
 * WB-104 (walk 3 Oct 2026): a trial that started mid-session (awaiting_activation
 * -> active) stayed "hasn't started counting yet" in Settings until a reload,
 * because nothing refetched it while the window kept focus. Settings now asks
 * for a quiet re-check on mount, and while the trial is awaiting activation we
 * re-check when the tab becomes visible and once a minute while it stays
 * visible (capped, so an open tab never polls for the whole 72h grace window).
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchTrialState, type TrialState } from '../services/trialService';
import { useAuth } from './AuthContext';

interface RefreshOptions {
  /**
   * Background re-check: leaves `loading` alone (useShouldPaywall reads it, so
   * toggling it flashes paywalled UI), keeps the last good state if the lookup
   * fails, and is skipped when a fetch ran in the last few seconds.
   */
  silent?: boolean;
}

interface TrialContextValue {
  trial: TrialState | null;
  loading: boolean;
  refresh: (options?: RefreshOptions) => Promise<void>;
}

/** Re-check interval while the trial is awaiting activation and the tab is visible. */
export const AWAITING_POLL_MS = 60_000;
/** Polls per visible stretch (30 min); becoming visible again starts a new stretch. */
export const AWAITING_MAX_POLLS = 30;
/** Silent re-checks closer together than this are skipped. */
export const SILENT_MIN_GAP_MS = 5_000;

const TrialContext = createContext<TrialContextValue>({
  trial: null,
  loading: false,
  refresh: async () => {},
});

export const TrialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [trial, setTrial] = useState<TrialState | null>(null);
  const [loading, setLoading] = useState(false);
  const lastFetchAtRef = useRef(0);
  // WB-104 QA: fetches can overlap (a quiet poll, then a focus refresh). Each one
  // gets a number, and a reply older than one already applied is dropped, so a
  // slow stale reply can't flip a started trial back to awaiting_activation.
  const fetchSeqRef = useRef(0);
  const appliedSeqRef = useRef(0);
  const userIdRef = useRef<string | undefined>(user?.id);
  userIdRef.current = user?.id;

  const refresh = useCallback(async (options?: RefreshOptions) => {
    if (!user) {
      setTrial(null);
      return;
    }
    if (options?.silent) {
      if (Date.now() - lastFetchAtRef.current < SILENT_MIN_GAP_MS) return;
      lastFetchAtRef.current = Date.now();
      const requestedFor = user.id;
      const seq = ++fetchSeqRef.current;
      const next = await fetchTrialState();
      if (next && userIdRef.current === requestedFor && seq > appliedSeqRef.current) {
        appliedSeqRef.current = seq;
        setTrial(next);
      }
      return;
    }
    lastFetchAtRef.current = Date.now();
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const next = await fetchTrialState();
      if (seq > appliedSeqRef.current) {
        appliedSeqRef.current = seq;
        setTrial(next);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch + auth-change fetch.
  useEffect(() => {
    if (authLoading) return;
    void refresh();
  }, [authLoading, user?.id, refresh]);

  // Refresh on window focus and on 402 trial:locked broadcasts.
  useEffect(() => {
    if (!user) return;
    const onFocus = () => void refresh();
    const onLocked = () => void refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('trial:locked', onLocked as EventListener);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('trial:locked', onLocked as EventListener);
    };
  }, [user, refresh]);

  // WB-104: while the clock has not started, re-check when the tab comes back
  // into view and once a minute while it stays visible.
  const awaitingActivation = trial?.state === 'awaiting_activation';
  useEffect(() => {
    if (!user || !awaitingActivation) return;
    let polls = 0;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || polls >= AWAITING_MAX_POLLS) return;
      polls += 1;
      void refresh({ silent: true });
    }, AWAITING_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      polls = 0;
      void refresh({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, awaitingActivation, refresh]);

  return (
    <TrialContext.Provider value={{ trial, loading, refresh }}>
      {children}
    </TrialContext.Provider>
  );
};

export function useTrial(): TrialContextValue {
  return useContext(TrialContext);
}

/**
 * Returns false ONLY when we KNOW this is an un-paid trial (loaded + no paid access).
 * While loading, or if the trial-state lookup failed (trial===null), treat as paid so
 * we never flash "Upgrade" at a paying customer or fail closed (mirrors the backend's
 * fail-open posture). The server is the real enforcement; this is UX.
 */
export function useShouldPaywall(): boolean {
  const { trial, loading } = useTrial();
  return !loading && !!trial && !trial.hasPaidAccess;
}
